import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import * as THREE from "three";
import { Html } from "@react-three/drei";
import {
  Popover,
  ActionIcon,
  Group,
  MantineProvider,
  Text,
  Textarea,
  Box,
} from "@mantine/core";
import { useField } from '@mantine/form';

import { IconSend, IconTrash, IconMessageCircle } from '@tabler/icons-react';
import { MoveCommentButton } from "./AddCommentButton"
import { useObjectStore } from "../zustand/objects";
import {
  scheduleScrollThreadCommentsToBottom,
  scrollThreadCommentsToBottom,
} from "../utils/threadComments";
import { editorModalAccent, editorModalBorder, editorModalShell } from "../styleFile";

const threadIconBtnStyles = (hover, theme) => ({
  root: {
    width: 32,
    height: 32,
    minWidth: 32,
    minHeight: 32,
    borderRadius: editorModalShell.radius,
    border: `1px solid ${hover ? theme.iconHoverBorder : theme.iconBorder}`,
    backgroundColor: hover ? theme.iconHoverBg : theme.iconBg,
    color: hover ? theme.iconHover : theme.icon,
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
  },
});

const InSceneComment = ({ id, position, artboard, currentObjectArtboard }) => {

  const [hover2, setHover2] = useState(false);
  const [open, setOpen] = useState(false);
  const [markerHover, setMarkerHover] = useState(false);

  const commentsScrollRef = useRef(null);
  const lastOpenThreadRef = useRef(null);
  const lastCommentCountRef = useRef(0);
  const popoverContentRef = useRef(null); // Ref for the Popover.Dropdown content
  const targetElementRef = useRef(null); // Ref for the Popover.Target div

  const {
    addComment,
    deleteThread,
    loadThreads,
    loadComments: storeLoadComments,
    viewedComments,
    viewedThread,
    setViewedThread,
    scrubViewedComments,
    viewportThemeOverride,
  } = useObjectStore();
  const isDark = viewportThemeOverride === "dark";
  const commentTheme = isDark
    ? {
        shellBg: "#2f2f2f",
        shellBorder: "#475569",
        headerBg: "#2f2f2f",
        markerBg: "#2f2f2f",
        markerHoverBg: "#2f2f2f",
        markerBorder: "#4B5563",
        markerActiveBorder: "#E0E0E0",
        markerIcon: "#9CA3AF",
        markerIconHover: "#E0E0E0",
        markerIconActive: "#E0E0E0",
        iconBg: "#2f2f2f",
        iconHoverBg: "#2f2f2f",
        iconBorder: "#4B5563",
        iconHoverBorder: "#E0E0E0",
        icon: "#9CA3AF",
        iconHover: "#E0E0E0",
        textareaText: "#E2E8F0",
        textareaBg: "#2f2f2f",
        textareaBorder: "#4B5563",
      }
    : {
        shellBg: "#FFFFFF",
        shellBorder: editorModalBorder,
        headerBg: "#FAFAFA",
        markerBg: "#FFFFFF",
        markerHoverBg: "#F9FAFB",
        markerBorder: editorModalBorder,
        markerActiveBorder: editorModalAccent,
        markerIcon: "#6B7280",
        markerIconHover: "#374151",
        markerIconActive: "#111827",
        iconBg: "#FFFFFF",
        iconHoverBg: "#F9FAFB",
        iconBorder: editorModalBorder,
        iconHoverBorder: "#D1D5DB",
        icon: "#6B7280",
        iconHover: "#374151",
        textareaText: "#111827",
        textareaBg: "#FFFFFF",
        textareaBorder: editorModalBorder,
      };

  const handleDelete = () => {
    deleteThread(id)
    setTimeout(() => {
      loadThreads()
    }, "500");
  }


  const handlePopOpen = (passedId) => {
    setOpen(true);
    setViewedThread(passedId);
    storeLoadComments(passedId);
  };

  const handlePopClose = useCallback(() => {
    setOpen(false)
    scrubViewedComments()
    setViewedThread(null)
  }, [scrubViewedComments, setViewedThread])

  useLayoutEffect(() => {
    if (viewedThread !== id) {
      lastOpenThreadRef.current = null;
      return;
    }
    if (viewedComments.length === 0) return;

    const len = viewedComments.length;
    const justOpenedThisMarker = lastOpenThreadRef.current !== id;
    lastOpenThreadRef.current = id;

    const el = commentsScrollRef.current;
    const schedule = () =>
      scheduleScrollThreadCommentsToBottom(() => commentsScrollRef.current);

    if (justOpenedThisMarker) {
      lastCommentCountRef.current = len;
      scrollThreadCommentsToBottom(el);
      schedule();
      return;
    }

    const grew = len > lastCommentCountRef.current;
    lastCommentCountRef.current = len;
    if (grew) {
      scrollThreadCommentsToBottom(el);
      schedule();
    }
  }, [id, viewedThread, viewedComments]);

  useEffect(() => {
    viewedThread === id && setOpen(true)
  }, [id, viewedThread])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        popoverContentRef.current &&
        !popoverContentRef.current.contains(event.target) &&
        targetElementRef.current &&
        !targetElementRef.current.contains(event.target) &&
        viewedThread === id
      ) {
        handlePopClose();
      }
    };

    open && document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handlePopClose, id, open, viewedThread]);


  return (
    <Html
      position={new THREE.Vector3(position.x, position.y, position.z)}
      as='div' // Wrapping element (default: 'div')
      wrapperClass // The className of the wrapping element (default: undefined)
      prepend // Project content behind the canvas (default: false)
      fullscreen // Aligns to the upper-left corner, fills the screen (default:false) [ignored in transform mode]
      distanceFactor={5} // If set (default: undefined), children will be scaled by this factor, and also by distance to a PerspectiveCamera / zoom by a OrthographicCamera.
      zIndexRange={[100, 0]} // Z-order range (default=[16777271, 0])
      transform // If true, applies matrix3d transformations (default=false)
      sprite // Renders as sprite, but only in transform mode (default=false)
    >
      <MantineProvider>
        <Popover
          closeOnClickOutside
          opened={viewedThread === id}
          onClose={handlePopClose}
          width={320}
          trapFocus
          position="bottom"
          withArrow={false}
          shadow="none"
          radius={editorModalShell.radius}
          styles={{
            dropdown: {
              border: `1px solid ${commentTheme.shellBorder}`,
              borderRadius: editorModalShell.radius,
              boxShadow: editorModalShell.styles.content.boxShadow,
              padding: 0,
              backgroundColor: commentTheme.shellBg,
              overflow: "hidden",
            },
          }}
        >
          <Popover.Target>
            <div
              ref={targetElementRef}
              onClick={() => {
                lastOpenThreadRef.current = null;
                handlePopOpen(id);
              }}
              onPointerEnter={() => setMarkerHover(true)}
              onPointerLeave={() => setMarkerHover(false)}
              style={{
                width: 36,
                height: 36,
                backgroundColor: markerHover ? commentTheme.markerHoverBg : commentTheme.markerBg,
                borderRadius: 4,
                border: `1px solid ${
                  viewedThread === id ? commentTheme.markerActiveBorder : markerHover ? commentTheme.markerBorder : commentTheme.markerBorder
                }`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 1px 2px rgba(15, 23, 42, 0.05)",
                cursor: "pointer",
                transition: "background-color 0.15s ease, border-color 0.15s ease",
              }}
              aria-label="Scene comment"
            >
              <IconMessageCircle
                size={18}
                color={
                  viewedThread === id
                    ? commentTheme.markerIconActive
                    : markerHover
                      ? commentTheme.markerIconHover
                      : commentTheme.markerIcon
                }
              />
            </div>
          </Popover.Target>
          <Popover.Dropdown ref={popoverContentRef} p={0}>
            <Box
              style={{
                borderBottom: `1px solid ${commentTheme.shellBorder}`,
                padding: "10px 12px",
                backgroundColor: commentTheme.headerBg,
              }}
            >
              <Group justify="flex-end" gap={6} wrap="nowrap">
                <MoveCommentButton threadsId={id} artboard={artboard} position={position} iconTheme={commentTheme} />
                <ActionIcon
                  variant="transparent"
                  aria-label="Delete thread"
                  onClick={() => handleDelete()}
                  onPointerOver={() => setHover2(true)}
                  onPointerLeave={() => setHover2(false)}
                  styles={threadIconBtnStyles(hover2, commentTheme)}
                >
                  <IconTrash size={18} />
                </ActionIcon>
              </Group>
            </Box>

            <Box
              ref={commentsScrollRef}
              style={{
                maxHeight: 220,
                overflowY: "auto",
                overflowX: "hidden",
                padding: "10px 12px",
              }}
            >
              {viewedThread === id &&
                viewedComments.map((mapped) => (
                  <UserComment
                    key={mapped.id}
                    id={mapped.id}
                    threadId={id}
                    userNameProp={mapped.author}
                    commentDateProp={new Date(mapped.created_at).toLocaleDateString()}
                    fullTextProp={mapped.comment_content}
                  />
                ))}
            </Box>

            <Box
              style={{
                borderTop: `1px solid ${commentTheme.shellBorder}`,
                padding: "10px 12px",
                backgroundColor: commentTheme.shellBg,
              }}
            >
              <CommentSubmit id={id} addComment={addComment} theme={commentTheme} />
            </Box>
          </Popover.Dropdown>
        </Popover>
      </MantineProvider>
    </Html>
  )
};

export default InSceneComment;

const CommentSubmit = ({ id, addComment, theme }) => {
  const [hover1, setHover1] = useState(false);

  const { currentObjectArtboard } = useObjectStore();

  const field = useField({
    initialValue: "",
    validateOnBlur: true,
    // Never run side effects inside validate — Mantine may call it on blur and again on send.
    validate: (value) =>
      value.trim().length < 1 ? "Please enter a value" : null,
  });

  const handleSend = () => {
    const val = String(field.getValue() ?? "").trim();
    if (!val) {
      field.setError("Please enter a value");
      return;
    }
    field.setError(null);
    addComment(id, val, currentObjectArtboard);
    field.setValue("");
    // Keep thread UI responsive while realtime event propagates.
    setTimeout(() => {
      useObjectStore.getState().loadComments(id);
    }, 120);
  };

  return (
    <Group align="flex-end" gap={8} wrap="nowrap" style={{ width: "100%" }}>
      <Textarea
        {...field.getInputProps()}
        placeholder="Leave a reply..."
        size="sm"
        minRows={2}
        style={{ flex: 1 }}
        styles={{
          input: {
            fontSize: 13,
            lineHeight: 1.45,
            color: theme.textareaText,
            border: `1px solid ${theme.textareaBorder}`,
            borderRadius: editorModalShell.radius,
            backgroundColor: theme.textareaBg,
            padding: "8px 10px",
          },
        }}
      />
      <ActionIcon
        variant="transparent"
        aria-label="Send reply"
        onPointerOver={() => setHover1(true)}
        onPointerLeave={() => setHover1(false)}
        onClick={() => handleSend()}
        styles={threadIconBtnStyles(hover1, theme)}
      >
        <IconSend size={18} />
      </ActionIcon>
    </Group>
  );
};


const UserComment = ({ id, threadId, userNameProp, commentDateProp, fullTextProp }) => {
  const { deleteComment, viewportThemeOverride } = useObjectStore();
  const [hover, setHover] = useState(false);
  const isDark = viewportThemeOverride === "dark";
  const theme = isDark
    ? {
        cardBg: "#2f2f2f",
        cardBorder: "#475569",
        user: "#E2E8F0",
        meta: "#9CA3AF",
        body: "#E2E8F0",
        iconBg: "#2f2f2f",
        iconHoverBg: "#2f2f2f",
        iconBorder: "#4B5563",
        iconHoverBorder: "#E0E0E0",
        icon: "#9CA3AF",
        iconHover: "#E0E0E0",
      }
    : {
        cardBg: "#FFFFFF",
        cardBorder: editorModalBorder,
        user: "#111827",
        meta: "#6A7282",
        body: "#374151",
        iconBg: "#FFFFFF",
        iconHoverBg: "#F9FAFB",
        iconBorder: editorModalBorder,
        iconHoverBorder: "#D1D5DB",
        icon: "#6B7280",
        iconHover: "#374151",
      };

  const handleDelete = () => {
    deleteComment(threadId, id);
  };

  return (
    <Box
      style={{
        marginBottom: 8,
        padding: "10px 12px",
        borderRadius: editorModalShell.radius,
        border: `1px solid ${theme.cardBorder}`,
        backgroundColor: theme.cardBg,
        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
      }}
    >
      <Group justify="space-between" align="flex-start" wrap="nowrap" gap={8} mb={6}>
        <Group gap={8} wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
          <Text
            size="xs"
            fw={600}
            c={theme.user}
            style={{
              fontFamily: "Inter, sans-serif",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: 140,
              textTransform: "uppercase",
            }}
          >
            {String(userNameProp ?? "").toUpperCase()}
          </Text>
          <Text
            size="xs"
            c={theme.meta}
            style={{
              flexShrink: 0,
            }}
          >
            {commentDateProp}
          </Text>
        </Group>
        <ActionIcon
          variant="transparent"
          aria-label="Delete comment"
          onClick={() => handleDelete()}
          onPointerOver={() => setHover(true)}
          onPointerLeave={() => setHover(false)}
          styles={threadIconBtnStyles(hover, theme)}
        >
          <IconTrash size={16} />
        </ActionIcon>
      </Group>
      <Text
        size="sm"
        c={theme.body}
        style={{
          lineHeight: 1.45,
        }}
      >
        {fullTextProp}
      </Text>
    </Box>
  );
};